import clsx from 'clsx';
import { memo, PropsWithChildren, ReactNode, useCallback, useContext } from 'react';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MESSAGES } from 'src/modules/common/constants';
import { PDFIcon } from 'src/modules/common/icons/PDFIcon';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { preventDefault } from 'src/modules/common/utils/preventDefault.ts';
import { RenderHtml } from 'src/modules/common/components/RenderHtml/RenderHtml.tsx';
import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { ConfigContext } from 'src/configContext';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { createFileGaClickEvent } from 'src/utils/createFileGaClickEvent.ts';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { RuntimeContext } from 'src/runtimeContext.ts';

type Props = PropsWithChildren<{
    selected?: boolean;
    accessory: AccessoriesPageData['vssAccessories']['valveAccessories'][0]['item'];
    chip?: ReactNode;
    price?: ReactNode;
    counter?: ReactNode;
    onSelect?: (label: string) => void;
    qty?: string;
    disabled?: boolean;
    theme?: 'white' | 'gray';
}>

export const Accessory = memo(({
    selected,
    accessory,
    chip,
    price,
    counter,
    onSelect,
    qty,
    disabled,
    theme,
    children,
}: Props) => {
    const { userSettings, gaPush } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const statical = onSelect === undefined;

    const onClick = useCallback(() => {
        if (onSelect) {
            onSelect(accessory.Label);
        }
    }, [onSelect, accessory]);

    const onFileClick = useCallback((url: string) => {
        gaPush(createFileGaClickEvent(url));
    }, [gaPush]);

    const handleSpecificationClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        if (accessory.specification) {
            const url = accessory.specification.localized.join('');
            onFileClick(url);
        }
    }, [accessory, onFileClick]);

    return (
        <div
            onKeyDown={() => null}
            role="button"
            tabIndex={0}
            className={clsx({
                'vss-accessory': true,
                'vss-accessory--selected': selected,
                'vss-accessory--statical': statical,
                'vss-accessory--disabled': disabled,
                'vss-accessory--gray': theme === 'gray',
            })}
            onClick={disabled ? () => null : onClick}
        >
            <div className="vss-accessory__header">
                <div className="vss-accessory__details">
                    <div className="vss-accessory__product">
                        {qty && (
                            <div className="vss-accessory__qty">
                                {qty}
                            </div>
                        )}

                        <ViewProductLink
                            userSettings={userSettings}
                            code={accessory.Label}
                            content=""
                        >
                            {accessory.Label}

                            <div className="vss-accessory__product-icon">
                                <ArrowRightIcon/>
                            </div>
                        </ViewProductLink>

                        {chip && (
                            <div className="vss-accessory__recommendation">
                                {chip}
                            </div>
                        )}
                    </div>
                </div>

                {children && (
                    <div className="vss-accessory__actions">
                        {children}
                    </div>
                )}

                {!statical && (
                    <div
                        data-testid="checkbox"
                        className={clsx({
                            'vss-accessory__checkbox': true,
                            'vss-accessory__checkbox--checked': selected,
                        })}
                    />
                )}
            </div>

            <div className="vss-accessory__body">
                <div className={clsx({
                    'vss-accessory__content-wrapper': true,
                    'vss-accessory__content-wrapper--mobile': isMobile,
                })}
                >
                    <div className="vss-accessory__image">
                        <ProductImage
                            imgSrc={accessory.image?.raw.join('') || ''}
                        >
                            <ActuatorIcon/>
                        </ProductImage>
                    </div>

                    <div className="vss-accessory__content">
                        <div
                            className="vss-accessory__type"
                            role="row"
                            tabIndex={0}
                            onKeyDown={() => null}
                            onClick={preventDefault}
                        >
                            <RenderHtml data={accessory.accessoryType?.localized.join('') || ''}/>
                        </div>

                        <div
                            className="vss-accessory__description"
                            role="row"
                            tabIndex={0}
                            onKeyDown={() => null}
                            onClick={preventDefault}
                        >
                            <RenderHtml data={accessory.description?.localized.join('') || ''}/>
                        </div>
                    </div>
                </div>

                <div className="vss-accessory__footer">
                    {price && (
                        <div className="vss-accessory__price-wrapper">
                            <div className="vss-accessory__price">
                                <div className="vss-accessory__price-label">
                                    <I18n messageId={MESSAGES.DevicePricePerUnit}/>
                                </div>

                                <div className="vss-accessory__price-value">
                                    {price}
                                </div>
                            </div>

                            {counter}
                        </div>
                    )}
                    <div className="vss-accessory__links">
                        {accessory.specification && (
                            <a
                                href={accessory.specification.localized.join('')}
                                onClick={handleSpecificationClick}
                                className="vss-link--secondary"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <span className="vss-link__text">
                                    <I18n messageId={MESSAGES._660_Data_sheet_PDF}/>
                                </span>
                                <span className="vss-link__icon">
                                    <PDFIcon/>
                                </span>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});
